/** Sanitize `?next=` — only allow same-origin paths starting with
 * `/`. Prevents an attacker from crafting `/login?next=//evil.com`
 * and bouncing operators offsite after sign-in.
 *
 * When `?next=` is absent or invalid, defaults to `/hub` — the
 * role-based redirect — so a bare sign-in lands each user on their
 * own home rather than the public marketing landing at `/`.
 */
export function sanitizeNextPath(next: string | undefined): string {
  if (!next || typeof next !== "string") return "/hub";
  if (next.length > 2048) return "/hub";
  if (!next.startsWith("/") || next.startsWith("//")) return "/hub";
  if (next.includes("\\")) return "/hub";
  if (/%5c/i.test(next)) return "/hub";
  if (isAuthEntryPath(next)) return "/hub";
  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1f\x7f]/.test(next)) return "/hub";
  // Drop the fragment. A `next` is built from the current location and then
  // travels through QUERY STRINGS — including a cross-host redirect to the
  // apex for social login — so a fragment riding along defeats the whole
  // reason a credential is put in one. `/guest#<token>` is the live case:
  // the guest share token is a reusable multi-day bearer credential that
  // must never reach an access log or a Referer.
  const hash = next.indexOf("#");
  return hash === -1 ? next : next.slice(0, hash);
}

export function isLegacyRequested(value: unknown): boolean {
  return value === "1" || value === "true" || value === 1 || value === true;
}

export function authSearchParam(name: string): string | undefined {
  const search =
    typeof window !== "undefined"
      ? window.location.search || globalThis.location?.search
      : globalThis.location?.search;
  if (!search) return undefined;
  return new URLSearchParams(search).get(name) ?? undefined;
}

function isAuthEntryPath(path: string): boolean {
  return /^\/(?:login|register|reset-password|verify-email)(?:[/?#]|$)/.test(path);
}
